import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { premieresApi } from '../lib/api.js';

// Tiny global store for the "current banner". The hook drops events into it
// and PremiereBanner reads from it. Keeping this separate from the auth /
// player stores so it doesn't trigger unrelated re-renders.
export const usePremiereDeliveryStore = create((set) => ({
  primary: null,
  alerts: [],
  source: 'idle', // 'sse' | 'polling' | 'idle'
  setPayload: (payload, source = 'sse') =>
    set({
      primary: payload?.switch || null,
      alerts: payload?.alerts || [],
      source,
    }),
  dismiss: () => set({ primary: null }),
}));

const POLL_INTERVAL_MS = 15_000;

export default function usePremiereStream(token) {
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!token) {
      usePremiereDeliveryStore.setState({ primary: null, alerts: [], source: 'idle' });
      return;
    }

    let source = null;
    let pollTimer = null;
    let cancelled = false;

    const setPayload = (payload, src) => {
      if (cancelled) return;
      usePremiereDeliveryStore.getState().setPayload(payload, src);
    };

    const startPolling = () => {
      usePremiereDeliveryStore.setState({ source: 'polling' });
      const tick = async () => {
        try {
          const data = await premieresApi.pendingSwitch();
          setPayload(data, 'polling');
        } catch { /* swallow */ }
      };
      tick();
      pollTimer = setInterval(tick, POLL_INTERVAL_MS);
    };

    const startSse = () => {
      try {
        source = new EventSource(premieresApi.eventsUrl(token));
      } catch {
        startPolling();
        return;
      }
      let opened = false;
      source.addEventListener('switch', (e) => setPayload(safeParse(e.data), 'sse'));
      source.addEventListener('alerts', (e) => setPayload(safeParse(e.data), 'sse'));
      source.addEventListener('ping', () => { /* keep-alive */ });
      source.onopen = () => { opened = true; usePremiereDeliveryStore.setState({ source: 'sse' }); };
      source.onerror = () => {
        // If we never opened, give up SSE and switch to polling.
        if (!opened) {
          source.close();
          source = null;
          startPolling();
        }
      };
    };

    startSse();

    return () => {
      cancelled = true;
      if (source) source.close();
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [token]);
}

function safeParse(raw) {
  try { return JSON.parse(raw); } catch { return null; }
}
